import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar, Clock, Loader2 } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { blogs } from "@/data/blogs";
import { fetchPublicBlog } from "@/lib/api";
import { normalizeBlog, useAllBlogs } from "@/pages/Blog";
import { breadcrumbJsonLd } from "@/lib/seo";

// Renders plain text with inline markdown links: [text](/path)
const renderPara = (text) => {
    const parts = String(text).split(/(\[[^\]]+\]\([^)]+\))/g);
    return parts.map((part, i) => {
        const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
        if (!m) return part;
        const cls = "text-gold-300/90 underline decoration-gold-300/30 underline-offset-4 transition hover:text-gold-300";
        return m[2].startsWith("/")
            ? <Link key={i} to={m[2]} className={cls}>{m[1]}</Link>
            : <a key={i} href={m[2]} target="_blank" rel="noreferrer" className={cls}>{m[1]}</a>;
    });
};

const articleJsonLd = (post) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.image && post.image.startsWith("http") ? post.image : `https://abhijit.pro${post.image || ""}`,
    author: { "@type": "Person", name: "Abhijit JK", url: "https://abhijit.pro/about" },
    publisher: { "@type": "Organization", name: "Abhijit.pro", url: "https://abhijit.pro" },
    mainEntityOfPage: `https://abhijit.pro/blog/${post.slug}`,
});

export default function BlogDetail() {
    const { slug } = useParams();
    const staticPost = blogs.find((b) => b.slug === slug);
    const [dynamicPost, setDynamicPost] = useState(null);
    const [loading, setLoading] = useState(!staticPost);
    const [notFound, setNotFound] = useState(false);
    const allBlogs = useAllBlogs();

    useEffect(() => {
        setDynamicPost(null);
        setNotFound(false);
        if (staticPost) { setLoading(false); return; }
        setLoading(true);
        fetchPublicBlog(slug)
            .then((doc) => setDynamicPost(normalizeBlog(doc)))
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
    }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

    if (notFound) return <Navigate to="/blog" replace />;
    if (loading) {
        return (
            <div className="flex min-h-[50vh] items-center justify-center" data-testid="blog-detail-loading">
                <Loader2 className="animate-spin text-gold-300" size={28} />
            </div>
        );
    }

    const post = staticPost ? normalizeBlog(staticPost) : dynamicPost;
    if (!post) return <Navigate to="/blog" replace />;
    const more = allBlogs.filter((b) => b.slug !== slug).slice(0, 3);

    return (
        <div data-testid="blog-detail-page" className="relative">
            <Seo
                title={post.meta_title || `${post.title} · Abhijit JK`}
                description={post.meta_description || post.excerpt}
                keywords={(post.keywords || []).join(", ") || undefined}
                path={`/blog/${post.slug}`}
                jsonLd={[
                    articleJsonLd(post),
                    breadcrumbJsonLd([
                        { name: "Home", path: "/" },
                        { name: "Blog", path: "/blog" },
                        { name: post.title, path: `/blog/${post.slug}` },
                    ]),
                ]}
            />

            <article className="mx-auto max-w-3xl px-6 pt-16 pb-10 lg:px-10">
                <Reveal>
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/55 transition hover:text-gold-300"
                    >
                        <ArrowLeft size={14} /> All Articles
                    </Link>
                </Reveal>
                <Reveal delay={0.05}>
                    <div className="mt-8 flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-[0.2em] text-gold-300/80">
                        <span>{post.category}</span>
                        <span className="inline-flex items-center gap-1.5 text-white/40">
                            <Calendar size={12} /> {post.date}
                        </span>
                        <span className="inline-flex items-center gap-1.5 text-white/40">
                            <Clock size={12} /> {post.readTime}
                        </span>
                    </div>
                </Reveal>
                <Reveal delay={0.1}>
                    <h1
                        data-testid="blog-detail-title"
                        className="mt-5 font-display text-3xl font-semibold leading-[1.1] tracking-tight sm:text-4xl lg:text-5xl"
                    >
                        {post.title}
                    </h1>
                </Reveal>
                {post.image && (
                    <Reveal delay={0.15}>
                        <div className="mt-8 aspect-[16/9] overflow-hidden rounded-lg">
                            <img
                                src={post.image}
                                alt={post.alt || post.title}
                                className="h-full w-full object-cover"
                            />
                        </div>
                    </Reveal>
                )}

                <div className="mt-10 space-y-6">
                    {(post.body || []).map((p, i) => (
                        <Reveal key={i} delay={0.04 * i}>
                            <p className="text-base leading-relaxed text-white/75 md:text-lg">
                                {renderPara(p)}
                            </p>
                        </Reveal>
                    ))}
                </div>

                <Reveal>
                    <div className="glass glow-border mt-14 rounded-lg p-8 text-center">
                        <h3 className="font-display text-2xl font-medium">
                            Want to understand where your business growth is{" "}
                            <span className="gold-text">stuck?</span>
                        </h3>
                        <p className="mx-auto mt-3 max-w-md text-sm text-white/60">
                            Book a Business Growth Diagnostic with Abhijit JK — an honest,
                            focused 60-minute one-to-one session with Abhijit JK on your brand, website and growth.
                        </p>
                        <Link
                            to="/consultation"
                            data-testid="blog-detail-cta"
                            className="gold-button mt-7 inline-flex items-center gap-2"
                        >
                            Book a Business Growth Diagnostic <ArrowRight size={14} />
                        </Link>
                    </div>
                </Reveal>

                <Reveal>
                    <div className="mt-10 flex flex-wrap gap-3">
                        {[
                            ["Home", "/"],
                            ["About Abhijit", "/about"],
                            ["Book Your Consultation — ₹1,999", "/consultation"],
                            ["Business Growth Workshops", "/workshops"],
                            ["Contact", "/contact"],
                        ].map(([label, to]) => (
                            <Link key={to} to={to} className="glass glass-hover inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs text-white/70 transition hover:text-gold-300">
                                {label} <ArrowRight size={11} />
                            </Link>
                        ))}
                    </div>
                </Reveal>
            </article>

            <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-10">
                <p className="label-eyebrow">More Articles</p>
                <div className="mt-8 grid gap-8 md:grid-cols-3">
                    {more.map((b) => (
                        <Link
                            key={b.slug}
                            to={`/blog/${b.slug}`}
                            className="group glass glow-border block overflow-hidden rounded-lg"
                        >
                            <div className="aspect-[16/10] overflow-hidden">
                                <img
                                    src={b.image}
                                    alt={b.alt || b.title}
                                    loading="lazy"
                                    className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                />
                            </div>
                            <div className="p-6">
                                <p className="text-[11px] uppercase tracking-[0.2em] text-gold-300/80">
                                    {b.category}
                                </p>
                                <h3 className="mt-3 font-display text-lg leading-snug">
                                    {b.title}
                                </h3>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
